/**
 * Main-thread controller for the worklet preview. The AudioContext is forced
 * to 48 kHz (the engine default) so preview, WAV export, and user-asset
 * decoding all run at one rate; the browser resamples to the device.
 */

import type { Preset } from "../../schema/types.ts";
import type { StereoBuffer } from "../dsp/renderer.ts";
import { DEFAULT_SAMPLE_RATE } from "../dsp/constants.ts";

export class Preview {
  onPosition: ((sec: number) => void) | null = null;
  onEnded: (() => void) | null = null;
  onError: ((message: string) => void) | null = null;

  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private gainNode: GainNode | null = null;
  private volume = 1;
  private pendingAssets: Array<{ ref: string; left: Float32Array; right: Float32Array }> = [];

  private async ensure(): Promise<AudioWorkletNode> {
    if (this.node) return this.node;
    this.ctx = new AudioContext({ sampleRate: DEFAULT_SAMPLE_RATE });
    await this.ctx.audioWorklet.addModule("./dist/processor.js");
    this.node = new AudioWorkletNode(this.ctx, "kalmtide-preview", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = this.volume;
    this.node.connect(this.gainNode);
    this.gainNode.connect(this.ctx.destination);
    this.node.port.onmessage = (e: MessageEvent) => {
      const msg = e.data as { type: string; sec?: number; message?: string };
      if (msg.type === "position") this.onPosition?.(msg.sec ?? 0);
      else if (msg.type === "ended") this.onEnded?.();
      else if (msg.type === "error") this.onError?.(msg.message ?? "preview error");
    };
    if (this.pendingAssets.length > 0) {
      this.node.port.postMessage({ type: "assets", assets: this.pendingAssets });
      this.pendingAssets = [];
    }
    return this.node;
  }

  get sampleRate(): number {
    return DEFAULT_SAMPLE_RATE;
  }

  /** Register user-decoded PCM (voice/ambience) with the worklet. */
  async addAsset(ref: string, buffer: StereoBuffer): Promise<void> {
    const payload = { ref, left: buffer.left, right: buffer.right };
    if (this.node) this.node.port.postMessage({ type: "assets", assets: [payload] });
    else this.pendingAssets.push(payload);
  }

  async load(preset: Preset): Promise<void> {
    const node = await this.ensure();
    node.port.postMessage({ type: "load", preset });
  }

  async play(): Promise<void> {
    const node = await this.ensure();
    await this.ctx!.resume();
    node.port.postMessage({ type: "play" });
  }

  pause(): void {
    this.node?.port.postMessage({ type: "pause" });
  }

  seek(sec: number): void {
    this.node?.port.postMessage({ type: "seek", sec });
  }

  /** Listening volume (0..1) — client-side only, never part of the preset. */
  setVolume(volume: number): void {
    this.volume = Math.min(1, Math.max(0, volume));
    if (this.gainNode && this.ctx) {
      // Short ramp avoids zipper noise on the volume slider.
      this.gainNode.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
    }
  }
}
